import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';
import type { AutocompleteProvider, HandlebarsBlockType } from '../../types';
import { BlockEditPopover } from '../../components/BlockEditPopover';
import { parseResolvedTokens, tokenStringToPlainText } from '../../utils/resolvedTokens';

export const HandlebarsBlockView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  selected,
  updateAttributes,
  extension,
}) => {
  const { blockType, variable, description, variableDescription, isValid, error, nonEditable } = node.attrs as {
    blockType: HandlebarsBlockType;
    variable: string;
    description: string | null;
    variableDescription: string | null;
    isValid: boolean;
    error: string | null;
    nonEditable: boolean;
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editAnchorRect, setEditAnchorRect] = useState<DOMRect | null>(null);

  const autocompleteProvider = extension.options.autocompleteProvider as AutocompleteProvider | undefined;

  const blockClasses = [
    'handlebars-block',
    `block-${blockType}`,
    selected ? 'selected' : '',
    !isValid ? 'invalid' : '',
    description ? 'has-description' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (autocompleteProvider) {
        const button = e.currentTarget as HTMLElement;
        setEditAnchorRect(button.getBoundingClientRect());
        setIsEditing(true);
      } else {
        // Fallback to prompt when no provider is configured
        const newVariable = prompt('Variable/condition:', variable);
        if (newVariable !== null) {
          updateAttributes({ variable: newVariable });
        }
      }
    },
    [variable, updateAttributes, autocompleteProvider]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowDeleteConfirm(true);
    },
    []
  );

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    editor.commands.focus();
  }, [editor]);

  const handleDeleteKeepContent = useCallback(() => {
    const pos = getPos();
    if (typeof pos !== 'number') return;

    // Replace the block with its children (lift content out)
    const { tr } = editor.state;
    const blockNode = tr.doc.nodeAt(pos);
    if (!blockNode) return;

    tr.replaceWith(pos, pos + blockNode.nodeSize, blockNode.content);
    editor.view.dispatch(tr);
    setShowDeleteConfirm(false);
  }, [editor, getPos]);

  const handleDeleteAll = useCallback(() => {
    const pos = getPos();
    if (typeof pos !== 'number') return;

    const blockNode = editor.state.doc.nodeAt(pos);
    if (!blockNode) return;

    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + blockNode.nodeSize })
      .run();

    setShowDeleteConfirm(false);
  }, [editor, getPos]);

  const variableTokens = useMemo(() => parseResolvedTokens(variableDescription), [variableDescription]);

  const renderVariable = () => {
    if (!variable) return null;
    if (variableTokens) {
      return (
        <span className="block-variable">
          ({variableTokens.map((token, i) =>
            token.isResolved
              ? <span key={i} className="resolved-token">{token.text}</span>
              : <span key={i}>{i > 0 ? ' ' : ''}{token.text}{i < variableTokens.length - 1 ? ' ' : ''}</span>
          )})
        </span>
      );
    }
    const plainText = tokenStringToPlainText(variableDescription) || variable;
    return <span className="block-variable">({plainText})</span>;
  };

  return (
    <NodeViewWrapper className={blockClasses}>
      <div className="handlebars-block-header" contentEditable={false}>
        <span className="block-indicator">#</span>
        {description ? (
          <>
            <span className="block-label">{description}</span>
            {renderVariable()}
          </>
        ) : (
          <>
            <span className="block-type">{blockType}</span>
            {renderVariable()}
          </>
        )}
        {error && (
          <span className="error-indicator" title={error}>
            !
          </span>
        )}
        {!nonEditable && (
          <div className="block-actions">
            <button
              type="button"
              className="block-action-btn"
              onClick={handleEdit}
              title="Edit block"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              type="button"
              className="block-action-btn danger"
              onClick={handleDeleteClick}
              title="Delete block"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <NodeViewContent className="handlebars-block-content" />

      <div className="handlebars-block-footer" contentEditable={false}>
        <span className="block-indicator">/</span>
        <span className="block-type">{description || blockType}</span>
        {description && renderVariable()}
      </div>

      {showDeleteConfirm && (
        <div className="block-delete-confirmation" contentEditable={false}>
          <span className="message">Delete block?</span>
          <span className="actions">
            <button type="button" onClick={handleDeleteKeepContent}>
              Keep content
            </button>
            <button type="button" className="danger" onClick={handleDeleteAll}>
              Delete all
            </button>
            <button type="button" onClick={handleDeleteCancel}>
              Cancel
            </button>
          </span>
        </div>
      )}

      {isEditing && editAnchorRect && autocompleteProvider && (
        <BlockEditPopover
          initialValue={variable}
          blockType={blockType}
          anchorRect={editAnchorRect}
          provider={autocompleteProvider}
          editor={editor}
          blockPos={typeof getPos() === 'number' ? getPos() as number : 0}
          showDescriptions={extension.options.showDescriptions as boolean}
          allowFreeformHandlebars={extension.options.allowFreeformHandlebars as boolean}
          onConfirm={(newVariable) => {
            updateAttributes({ variable: newVariable, variableDescription: null });
            setIsEditing(false);
            editor.commands.focus();
          }}
          onClose={() => {
            setIsEditing(false);
            editor.commands.focus();
          }}
        />
      )}
    </NodeViewWrapper>
  );
};

export default HandlebarsBlockView;
